// SPDX-License-Identifier: AGPL-3.0-or-later

import type {VoiceEngineEvent} from '@app/features/platform/types/Electron';
import {Logger} from '@app/features/platform/utils/AppLogger';
import type {VoiceEngineStats} from '@app/features/voice/engine/native_voice_engine/VoiceEngine';
import {
	asVoiceConnectionQuality,
	asVoiceTrackSource,
	type VoiceConnectionQuality,
	type VoiceTrackSource,
	VoiceTrackSource as VoiceTrackSourceValue,
} from '@app/features/voice/engine/VoiceTrackSource';

const logger = new Logger('NativeVoiceEngineEventMapper');

export interface NativeVoiceEngineEventManagers {
	participants: {
		upsertParticipantFromNative: (fields: {
			identity: string;
			sid: string;
			name?: string;
			isLocal?: boolean;
			metadata?: string;
			attributes?: Readonly<Record<string, string>>;
			isMicrophoneEnabled?: boolean;
			isCameraEnabled?: boolean;
			isScreenShareEnabled?: boolean;
		}) => void;
		patchParticipantTrackFlags: (
			identity: string,
			flags: {isMicrophoneEnabled?: boolean; isCameraEnabled?: boolean; isScreenShareEnabled?: boolean},
		) => void;
		setConnectionQualityForNative: (sid: string, quality: VoiceConnectionQuality) => void;
		updateActiveSpeakersBySid: (sids: ReadonlyArray<string>) => void;
		removeParticipant: (identity: string) => void;
		removeParticipantBySid: (sid: string) => void;
	};
	inboundVideo: {
		registerTrack: (participantSid: string, trackSid: string, source: string) => void;
		unregisterTrack: (trackSid: string) => void;
		unregisterParticipant: (participantSid: string) => void;
	};
	localMedia: {
		onLocalTrackPublished: (source: VoiceTrackSource) => void;
		onLocalTrackUnpublished?: (source: VoiceTrackSource) => void;
		onLocalTrackRepublished?: (source: VoiceTrackSource) => void;
	};
	e2ee: {
		setState: (sid: string, raw: unknown) => void;
		remove: (sid: string) => void;
	};
	stats: {
		setStats: (stats: VoiceEngineStats) => void;
	};
}

function asTrackKind(value: unknown): 'audio' | 'video' {
	return value === 'video' ? 'video' : 'audio';
}

function asNumber(value: unknown): number {
	return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function asOptionalNumber(value: unknown): number | undefined {
	return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function asOptionalCount(value: unknown): number | undefined {
	const number = asOptionalNumber(value);
	return number == null ? undefined : Math.max(0, Math.trunc(number));
}

export function coerceVoiceEngineStats(payload: Record<string, unknown>): VoiceEngineStats {
	const rttRaw = payload.rttMs;
	const rttMs = typeof rttRaw === 'number' && Number.isFinite(rttRaw) ? rttRaw : null;
	const droppedVideoFrameCallbacks = asOptionalCount(payload.droppedVideoFrameCallbacks);
	const outbound = Array.isArray(payload.outbound)
		? payload.outbound.flatMap((entry) => {
				if (entry == null || typeof entry !== 'object') return [];
				const e = entry as Record<string, unknown>;
				return [
					{
						trackSid: asString(e.trackSid) ?? '',
						source: asString(e.source) ?? '',
						kind: asTrackKind(e.kind),
						bitrateKbps: asNumber(e.bitrateKbps),
						packetsLost: asNumber(e.packetsLost),
						fps: asOptionalNumber(e.fps),
					},
				];
			})
		: [];
	const inbound = Array.isArray(payload.inbound)
		? payload.inbound.flatMap((entry) => {
				if (entry == null || typeof entry !== 'object') return [];
				const e = entry as Record<string, unknown>;
				return [
					{
						participantSid: asString(e.participantSid) ?? '',
						trackSid: asString(e.trackSid) ?? '',
						kind: asTrackKind(e.kind),
						bitrateKbps: asNumber(e.bitrateKbps),
						packetsLost: asNumber(e.packetsLost),
						jitterMs: asOptionalNumber(e.jitterMs),
						audioLevel: asOptionalNumber(e.audioLevel),
					},
				];
			})
		: [];
	return {rttMs, outbound, inbound, droppedVideoFrameCallbacks};
}

function asString(value: unknown): string | undefined {
	return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function asBoolean(value: unknown): boolean | undefined {
	return typeof value === 'boolean' ? value : undefined;
}

function asStringArray(value: unknown): Array<string> {
	if (!Array.isArray(value)) return [];
	return value.filter((entry): entry is string => typeof entry === 'string');
}

function asOptionalString(value: unknown): string | undefined {
	return typeof value === 'string' ? value : undefined;
}

function asStringRecord(value: unknown): Readonly<Record<string, string>> | undefined {
	if (value == null || typeof value !== 'object' || Array.isArray(value)) return undefined;
	const entries = Object.entries(value).filter((entry): entry is [string, string] => typeof entry[1] === 'string');
	return Object.freeze(Object.fromEntries(entries));
}

function asRecordArray(value: unknown): Array<Record<string, unknown>> {
	if (!Array.isArray(value)) return [];
	return value.filter((entry): entry is Record<string, unknown> => entry != null && typeof entry === 'object');
}

export function parseConnectionQuality(value: unknown): VoiceConnectionQuality {
	return asVoiceConnectionQuality(value);
}

function trackFlagsForSource(
	source: string | undefined,
	present: boolean,
): {
	isMicrophoneEnabled?: boolean;
	isCameraEnabled?: boolean;
	isScreenShareEnabled?: boolean;
} {
	switch (asVoiceTrackSource(source)) {
		case VoiceTrackSourceValue.Microphone:
			return {isMicrophoneEnabled: present};
		case VoiceTrackSourceValue.Camera:
			return {isCameraEnabled: present};
		case VoiceTrackSourceValue.ScreenShare:
			return {isScreenShareEnabled: present};
		default:
			return {};
	}
}

function participantNameFromPayload(payload: Record<string, unknown>): string | undefined {
	return asString(payload.participantName) ?? asString(payload.name);
}

function upsertParticipantFromPayload(
	payload: Record<string, unknown>,
	managers: NativeVoiceEngineEventManagers,
	sidKey: 'sid' | 'participantSid',
): string | undefined {
	const identity = asString(payload.identity);
	const sid = asString(payload[sidKey]);
	if (!identity || !sid) return identity;
	const name = participantNameFromPayload(payload);
	const fields: Parameters<NativeVoiceEngineEventManagers['participants']['upsertParticipantFromNative']>[0] = {
		identity,
		sid,
	};
	if (name) fields.name = name;
	const metadata = asOptionalString(payload.metadata);
	if (metadata != null) fields.metadata = metadata;
	const attributes = asStringRecord(payload.attributes);
	if (attributes) fields.attributes = attributes;
	managers.participants.upsertParticipantFromNative(fields);
	return identity;
}

function upsertLocalParticipantFromTrackPayload(
	payload: Record<string, unknown>,
	managers: NativeVoiceEngineEventManagers,
): string | undefined {
	const identity = asString(payload.identity);
	const sid = asString(payload.participantSid);
	if (!identity || !sid) return identity;
	const name = participantNameFromPayload(payload);
	managers.participants.upsertParticipantFromNative(
		name ? {identity, sid, name, isLocal: true} : {identity, sid, isLocal: true},
	);
	return identity;
}

function isPublishedTrackEnabled(payload: Record<string, unknown>, present: boolean): boolean {
	return present && asBoolean(payload.muted) !== true;
}

function patchParticipantTrackFlagsFromPayload(
	payload: Record<string, unknown>,
	managers: NativeVoiceEngineEventManagers,
	present: boolean,
): void {
	const identity = upsertParticipantFromPayload(payload, managers, 'participantSid');
	if (!identity) return;
	const source = asString(payload.source);
	managers.participants.patchParticipantTrackFlags(
		identity,
		trackFlagsForSource(source, isPublishedTrackEnabled(payload, present)),
	);
}

function patchLocalParticipantTrackFlagsFromPayload(
	payload: Record<string, unknown>,
	managers: NativeVoiceEngineEventManagers,
	present: boolean,
): void {
	const identity = upsertLocalParticipantFromTrackPayload(payload, managers);
	if (!identity) return;
	managers.participants.patchParticipantTrackFlags(
		identity,
		trackFlagsForSource(asString(payload.source), isPublishedTrackEnabled(payload, present)),
	);
}

export function isFacadeOwnedConnectionEvent(type: string): boolean {
	return (
		type === 'connected' ||
		type === 'Connected' ||
		type === 'disconnected' ||
		type === 'Disconnected' ||
		type === 'connectionState' ||
		type === 'Reconnecting' ||
		type === 'Reconnected'
	);
}

export type NativeVoiceEngineConnectionEventAction = 'connected' | 'disconnected' | 'reconnecting' | 'reconnected';

export function getNativeVoiceEngineConnectionEventAction(
	event: Pick<VoiceEngineEvent, 'type' | 'payload'>,
): NativeVoiceEngineConnectionEventAction | null {
	const payload = event.payload as Record<string, unknown>;
	switch (event.type) {
		case 'connected':
		case 'Connected':
			return 'connected';
		case 'disconnected':
		case 'Disconnected':
			return 'disconnected';
		case 'Reconnecting':
			return 'reconnecting';
		case 'Reconnected':
			return 'reconnected';
		case 'connectionState':
			switch (asString(payload.state)) {
				case 'connected':
					return 'connected';
				case 'disconnected':
					return 'disconnected';
				case 'reconnecting':
				case 'signalReconnecting':
				case 'signalreconnecting':
					return 'reconnecting';
				default:
					return null;
			}
		default:
			return null;
	}
}

export function mapNativeVoiceEngineEvent(event: VoiceEngineEvent, managers: NativeVoiceEngineEventManagers): void {
	const payload = event.payload as Record<string, unknown>;
	switch (event.type) {
		case 'connectionState':
			logger.debug('Native engine connection state', {state: payload.state});
			break;
		case 'participantJoined': {
			const identity = asString(payload.identity);
			const sid = asString(payload.sid);
			if (!identity || !sid) {
				logger.warn('participantJoined missing identity/sid', {payload});
				break;
			}
			managers.participants.upsertParticipantFromNative({identity, sid, name: asString(payload.name)});
			break;
		}
		case 'participantNameChanged': {
			const identity = asString(payload.identity);
			const sid = asString(payload.sid);
			const name = asString(payload.name);
			if (!identity || !sid || !name) {
				logger.warn('participantNameChanged missing identity/sid/name', {payload});
				break;
			}
			managers.participants.upsertParticipantFromNative({identity, sid, name});
			break;
		}
		case 'participantMetadataChanged':
		case 'participantAttributesChanged': {
			const identity = upsertParticipantFromPayload(payload, managers, 'sid');
			if (!identity) {
				logger.warn(`${event.type} missing identity/sid`, {payload});
			}
			break;
		}
		case 'participantLeft': {
			const identity = asString(payload.identity);
			const sid = asString(payload.sid);
			if (identity) {
				managers.participants.removeParticipant(identity);
			} else if (sid) {
				managers.participants.removeParticipantBySid(sid);
			}
			if (sid) {
				managers.inboundVideo.unregisterParticipant(sid);
				managers.e2ee.remove(sid);
			}
			break;
		}
		case 'trackPublished': {
			patchParticipantTrackFlagsFromPayload(payload, managers, true);
			break;
		}
		case 'trackUnpublished': {
			patchParticipantTrackFlagsFromPayload(payload, managers, false);
			const trackSid = asString(payload.trackSid);
			if (trackSid) {
				managers.inboundVideo.unregisterTrack(trackSid);
			}
			break;
		}
		case 'trackSubscribed': {
			const participantSid = asString(payload.participantSid);
			const trackSid = asString(payload.trackSid);
			const kind = asString(payload.kind);
			const source = asString(payload.source);
			if (!participantSid || !trackSid) {
				logger.warn('trackSubscribed missing participantSid/trackSid', {payload});
				break;
			}
			patchParticipantTrackFlagsFromPayload(payload, managers, true);
			if (kind === 'video') {
				managers.inboundVideo.registerTrack(participantSid, trackSid, asVoiceTrackSource(source));
			}
			break;
		}
		case 'trackUnsubscribed': {
			const participantSid = asString(payload.participantSid);
			const trackSid = asString(payload.trackSid);
			if (!trackSid) {
				logger.warn('trackUnsubscribed missing trackSid', {payload});
				break;
			}
			managers.inboundVideo.unregisterTrack(trackSid);
			if (!participantSid) {
				logger.debug('trackUnsubscribed without participantSid; tile torn down by trackSid only', {trackSid});
			}
			break;
		}
		case 'trackMuted':
		case 'trackUnmuted': {
			patchParticipantTrackFlagsFromPayload(payload, managers, event.type === 'trackUnmuted');
			break;
		}
		case 'trackSubscriptionFailed': {
			const trackSid = asString(payload.trackSid);
			if (trackSid) {
				managers.inboundVideo.unregisterTrack(trackSid);
			}
			break;
		}
		case 'activeSpeakers': {
			for (const participant of asRecordArray(payload.participants)) {
				const identity = asString(participant.identity);
				const sid = asString(participant.sid);
				if (identity && sid) {
					const name = asString(participant.name);
					managers.participants.upsertParticipantFromNative(name ? {identity, sid, name} : {identity, sid});
				}
			}
			managers.participants.updateActiveSpeakersBySid(asStringArray(payload.sids));
			break;
		}
		case 'connectionQuality': {
			const sid = asString(payload.sid);
			if (!sid) {
				logger.warn('connectionQuality missing sid', {payload});
				break;
			}
			upsertParticipantFromPayload(payload, managers, 'sid');
			managers.participants.setConnectionQualityForNative(sid, parseConnectionQuality(payload.quality));
			break;
		}
		case 'e2eeState': {
			const sid = asString(payload.sid);
			if (!sid) {
				logger.warn('e2eeState missing sid', {payload});
				break;
			}
			upsertParticipantFromPayload(payload, managers, 'sid');
			managers.e2ee.setState(sid, payload.state);
			break;
		}
		case 'stats': {
			managers.stats.setStats(coerceVoiceEngineStats(payload));
			break;
		}
		case 'audioPlaybackUnavailable': {
			logger.warn('Native voice engine audio playback unavailable', {message: asString(payload.message)});
			break;
		}
		case 'localTrackPublished': {
			patchLocalParticipantTrackFlagsFromPayload(payload, managers, true);
			managers.localMedia.onLocalTrackPublished(asVoiceTrackSource(payload.source));
			break;
		}
		case 'localTrackUnpublished': {
			patchLocalParticipantTrackFlagsFromPayload(payload, managers, false);
			managers.localMedia.onLocalTrackUnpublished?.(asVoiceTrackSource(payload.source));
			break;
		}
		case 'localTrackRepublished': {
			const source = asVoiceTrackSource(payload.source);
			patchLocalParticipantTrackFlagsFromPayload(payload, managers, true);
			managers.localMedia.onLocalTrackRepublished?.(source);
			managers.localMedia.onLocalTrackPublished(source);
			break;
		}
		default:
			logger.debug('Native voice engine event (unmapped)', {type: event.type});
			break;
	}
}
